import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { courseAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Lock, Heart, Loader2, ChevronRight, Award, Send,
} from 'lucide-react';

function DayList({ days, onSelect }) {
  return (
    <div className="space-y-2" data-testid="day-list">
      {days.map((d, i) => {
        const isCompleted = d.status === 'completed';
        const isLocked = d.status === 'locked';
        return (
          <motion.button
            key={d.day_num}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            disabled={isLocked}
            onClick={() => onSelect(d.day_num)}
            className={`w-full text-left p-4 rounded-xl border transition-all ${
              isLocked ? 'opacity-40 cursor-not-allowed border-white/5' :
              isCompleted ? 'border-green-500/30 hover:bg-green-500/5' :
              'border-rose-500/20 hover:bg-rose-500/5 hover:border-rose-500/40'
            }`}
            data-testid={`day-item-${d.day_num}`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                isCompleted ? 'bg-green-500/15' : isLocked ? 'bg-white/5' : 'bg-rose-500/15'
              }`}>
                {isCompleted ? <Check className="h-5 w-5 text-green-400" /> :
                 isLocked ? <Lock className="h-4 w-4 text-gray-600" /> :
                 <span className="text-sm font-bold text-rose-400">{d.day_num}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${isLocked ? 'text-gray-600' : 'text-white'}`}>Dia {d.day_num}</p>
                <p className={`text-xs truncate ${isLocked ? 'text-gray-700' : 'text-gray-400'}`}>{d.title}</p>
              </div>
              {!isLocked && <ChevronRight className={`h-4 w-4 ${isCompleted ? 'text-green-400' : 'text-gray-500'}`} />}
            </div>
          </motion.button>
        );
      })}
    </div>
  );
}

function DayContent({ dayData, onSubmit, onBack, submitting }) {
  const [answers, setAnswers] = useState({});
  const isCompleted = dayData.is_completed;

  const handleSelect = (qNum, letter) => {
    if (isCompleted) return;
    setAnswers(prev => ({ ...prev, [qNum]: letter }));
  };

  const handleSubmit = () => {
    const formatted = Object.entries(answers).map(([qNum, letter]) => ({
      question_number: parseInt(qNum),
      answer: letter,
    }));
    if (formatted.length === 0) {
      toast.error('Responda pelo menos uma questão');
      return;
    }
    onSubmit(formatted);
  };

  return (
    <div data-testid="day-content">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="text-gray-500 hover:text-gray-300" data-testid="back-to-days"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex-1">
          <p className="text-xs text-rose-400 font-medium">Dia {dayData.day_num}</p>
          <h2 className="text-lg font-bold text-white">{dayData.title}</h2>
        </div>
        {isCompleted && dayData.submission && (
          <span className="text-xs bg-green-500/15 text-green-400 px-3 py-1 rounded-full font-medium">
            {dayData.submission.score}/{dayData.questions.length * 2} pts
          </span>
        )}
      </div>

      {/* Orientation */}
      {dayData.orientation && (
        <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(244,114,182,0.06)', border: '1px solid rgba(244,114,182,0.2)' }}>
          <p className="text-xs text-rose-400 font-medium mb-2">Orientação do Dia</p>
          <p className="text-sm text-gray-300 leading-relaxed">{dayData.orientation}</p>
        </div>
      )}

      {/* Activity */}
      {dayData.activity && (
        <div className="rounded-xl p-4 mb-6" style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.2)' }}>
          <p className="text-xs text-purple-400 font-medium mb-2">Atividade Prática</p>
          <p className="text-sm text-gray-300 leading-relaxed">{dayData.activity}</p>
        </div>
      )}

      {/* Quiz */}
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">Questionário do Dia</p>
      <div className="space-y-4 mb-6">
        {dayData.questions.map((q) => {
          const savedAnswer = isCompleted && dayData.submission?.answers
            ? dayData.submission.answers.find(a => a.question_number === q.number)?.answer
            : null;
          const userAnswer = savedAnswer || answers[q.number];
          const correctAnswer = isCompleted ? q.correct : null;

          return (
            <div key={q.number} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid={`family-q-${q.number}`}>
              <p className="text-sm text-gray-200 mb-3">{q.number}. {q.text}</p>
              <div className="space-y-2">
                {q.options.map((opt) => {
                  const isSelected = userAnswer === opt.letter;
                  const isCorrect = correctAnswer === opt.letter;
                  let bgStyle = {};
                  if (isCompleted && isCorrect) bgStyle = { background: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.4)' };
                  else if (isCompleted && isSelected && !isCorrect) bgStyle = { background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.4)' };
                  else if (isSelected) bgStyle = { background: 'rgba(244,114,182,0.1)', borderColor: 'rgba(244,114,182,0.4)' };

                  return (
                    <button
                      key={opt.letter}
                      disabled={isCompleted}
                      onClick={() => handleSelect(q.number, opt.letter)}
                      className={`w-full text-left p-3 rounded-lg border text-sm transition-all ${isCompleted ? 'cursor-default' : 'hover:bg-white/3'} ${isSelected && !isCompleted ? '' : 'border-white/8 text-gray-400'}`}
                      style={bgStyle}
                      data-testid={`family-opt-${q.number}-${opt.letter}`}
                    >
                      <span className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded-full border flex items-center justify-center text-xs flex-shrink-0 ${
                          isSelected ? 'border-rose-400 text-rose-400 font-bold' : 'border-white/20 text-gray-600'
                        }`}>{opt.letter}</span>
                        <span className={isSelected ? 'text-white' : ''}>{opt.text}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {isCompleted && q.explanation && (
                <p className="text-xs text-gray-500 mt-2 italic">{q.explanation}</p>
              )}
            </div>
          );
        })}
      </div>

      {!isCompleted && (
        <button
          onClick={handleSubmit}
          disabled={submitting || Object.keys(answers).length === 0}
          className="w-full py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-40 transition-all flex items-center justify-center gap-2"
          style={{ background: 'linear-gradient(135deg, #f472b6, #ec4899)' }}
          data-testid="submit-family-quiz"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {submitting ? 'Enviando...' : 'Concluir Dia'}
        </button>
      )}
    </div>
  );
}

export default function CourseFamilyPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [days, setDays] = useState([]);
  const [progress, setProgress] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDay, setLoadingDay] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState('list');

  const loadDays = useCallback(async () => {
    try {
      const res = await courseAPI.getFamilyDays();
      setDays(res.data.days);
      setProgress(res.data);
    } catch (err) {
      toast.error('Erro ao carregar programa');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) loadDays();
  }, [authLoading, user, loadDays]);

  const handleSelectDay = async (num) => {
    setLoadingDay(true);
    setView('day');
    try {
      const res = await courseAPI.getFamilyDay(num);
      setDayData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar dia');
      setView('list');
    } finally {
      setLoadingDay(false);
    }
  };

  const handleSubmit = async (answers) => {
    if (!dayData) return;
    setSubmitting(true);
    try {
      const res = await courseAPI.submitFamilyQuiz(dayData.day_num, answers);
      toast.success(`Dia ${dayData.day_num} concluído! ${res.data.score}/${res.data.max_score} pontos · +${res.data.tokens_awarded} AWA`);
      await loadDays();
      setView('list');
      setDayData(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #06061a 0%, #1a0a1a 50%, #0a0a1a 100%)' }}>
        <Loader2 className="h-8 w-8 animate-spin text-rose-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12" style={{ background: 'linear-gradient(180deg, #06061a 0%, #1a0a1a 50%, #0a0a1a 100%)' }} data-testid="course-family-page">
      <div className="max-w-2xl mx-auto px-4 pt-6">
        {view === 'list' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-300" data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></button>
              <div className="flex-1">
                <h1 className="text-xl font-bold text-white">Programa Familiar</h1>
                <p className="text-xs text-gray-500">Plano de 90 Dias · Questionários Diários</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-rose-400">{progress?.completed_count || 0}/{progress?.total_days || 0}</p>
                <p className="text-xs text-gray-600">dias</p>
              </div>
            </div>

            <div className="mb-6 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <motion.div className="h-full rounded-full" style={{ background: 'linear-gradient(90deg, #f472b6, #ec4899)' }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progress?.total_days ? ((progress?.completed_count || 0) / progress.total_days) * 100 : 0}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">5 perguntas por dia · 2 pontos por acerto · Meta: 720 pontos</p>
            </div>

            <DayList days={days} onSelect={handleSelectDay} />
          </>
        )}

        {view === 'day' && (
          loadingDay ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-rose-400" /></div>
          ) : dayData ? (
            <DayContent dayData={dayData} onSubmit={handleSubmit} onBack={() => { setView('list'); setDayData(null); }} submitting={submitting} />
          ) : null
        )}
      </div>
    </div>
  );
}
